import React, { useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core';
import MUITabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import AppBar from '@material-ui/core/AppBar';
import tabPropType from './propTypes';
import TabContentContainer from './TabContentContainer';
import FormContext from '../FormContext';
import useActiveTab from './useActiveTab';

const useStyles = makeStyles(theme => ({
  appBar: {
    boxShadow: 'none',
    background: theme.palette.grey[100],
    flex: '1 1 auto'
  },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.grey[300]}`
  },
  scrollButton: {
    '&[aria-disabled="false"]': {
      backgroundColor: '#ededed',
      color: '#0058a3'
    },
    '&[aria-disabled="true"]': {
      backgroundColor: 'inherit'
    }
  }
}));

const Tabs = ({ tabs = [] }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const formContext = useContext(FormContext);

  const getVisibleTabs = useCallback(() => {
    return tabs.filter(({ visible = () => true }) => visible(formContext.data));
  }, [tabs, formContext]);

  const [activeTab, setActiveTab, onChangeTab] = useActiveTab(getVisibleTabs());

  const isTabExisting = useCallback(() => {
    return getVisibleTabs()
      .map(tab => tab.name)
      .includes(activeTab.name);
  }, [getVisibleTabs, activeTab]);

  useEffect(() => {
    // if (actTab) {
    //   setActiveTab(actTab)
    // } else
    if (!isTabExisting()) {
      setActiveTab(getVisibleTabs()[0]);
    }
  }, [isTabExisting, getVisibleTabs, setActiveTab]);

  function renderTabs() {
    return getVisibleTabs().map(({ name, label }) => (
      <Tab
        value={name}
        label={formatMessage({ id: label })}
        key={`tab/${name}`}
      />
    ));
  }

  if (!isTabExisting()) {
    return null;
  }

  return (
    <>
      <div className={classes.tabs} data-testid="form-tabs">
        <AppBar className={classes.appBar} position="static">
          <MUITabs
            value={activeTab.name}
            onChange={onChangeTab}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
            variant="scrollable"
            classes={{
              scrollButtons: classes.scrollButton // Apply custom style here
            }}
          >
            {renderTabs()}
          </MUITabs>
        </AppBar>
      </div>
      <TabContentContainer
        TabContent={activeTab.TabContent}
        tabContentPadding={activeTab.tabContentPadding}
      />
    </>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape(tabPropType)).isRequired
};

export default Tabs;
